/**
 * 프로젝트 타입별 점검항목 필드 매핑
 *
 * 주의: 이 파일은 클라이언트 사이드에서도 안전하게 사용 가능
 * type-graphql/reflect-metadata 의존성 없음
 */

/**
 * 점검 타입 (내부용)
 */
export type InspectionType = "PERFORMANCE_CHECK" | "MAINTENANCE";

/**
 * 프로젝트 타입에 따른 필드 매핑 인터페이스
 */
export interface InspectionTypeFields {
  /** 점검항목 배열 필드명 */
  checkListField: "checkList" | "maintenance";
  /** 부적합사항 필드명 (CheckItem 레벨) */
  underReasonField: "underReason";
  /** 조치필요사항 필드명 (CheckItem 레벨) */
  underSolutionField: "underSolution";
  /** 템플릿 배열 필드명 */
  templateField: "checkListTemplate" | "maintenanceTemplate";
  /** GraphQL itemType */
  itemType: "checkList" | "maintenance";
}

/**
 * 프로젝트 타입별 필드 매핑 상수
 *
 * 참고:
 * - underReason/underSolution은 CheckItem 스키마의 필드 (두 타입 모두 동일)
 * - underReasonMT/underSolutionMT는 MachineDocument 레벨 필드 (문서 전체)
 */
export const INSPECTION_TYPE_FIELDS: Record<InspectionType, InspectionTypeFields> = {
  PERFORMANCE_CHECK: {
    checkListField: "checkList",
    underReasonField: "underReason",
    underSolutionField: "underSolution",
    templateField: "checkListTemplate",
    itemType: "checkList",
  },
  MAINTENANCE: {
    checkListField: "maintenance",
    underReasonField: "underReason",
    underSolutionField: "underSolution",
    templateField: "maintenanceTemplate",
    itemType: "maintenance",
  },
} as const;

/**
 * 프로젝트 타입 문자열을 InspectionType으로 변환
 *
 * 지원 입력:
 * - DocxInspectionType: "checklist", "maintenance" (권장)
 * - GraphQL enum: "PERFORMANCE_CHECK", "MAINTENANCE"
 * - 한글 (레거시): "성능점검", "유지관리"
 * - null/undefined: 기본값 "PERFORMANCE_CHECK" 반환
 * - 알 수 없는 문자열: 기본값 "PERFORMANCE_CHECK" 반환
 */
export function getInspectionType(projectType: string | null | undefined): InspectionType {
  if (!projectType) {
    return "PERFORMANCE_CHECK";
  }

  const normalized = projectType.trim();

  // 유지관리 타입 확인 (영문 DB값, 영문 enum, 한글 레거시)
  if (normalized === "maintenance" || normalized === "MAINTENANCE" || normalized === "유지관리") {
    return "MAINTENANCE";
  }

  // 기본값: 성능점검 (checklist, PERFORMANCE_CHECK, 성능점검, 또는 기타)
  return "PERFORMANCE_CHECK";
}

/**
 * 프로젝트 타입에 따른 필드 정보 반환
 *
 * @param projectType - 프로젝트 타입 문자열 (checklist/maintenance, PERFORMANCE_CHECK/MAINTENANCE)
 * @returns InspectionTypeFields - 해당 타입에 대한 필드 매핑
 *
 * @example
 * ```typescript
 * const fields = getInspectionTypeFields("checklist");
 * // { checkListField: "checkList", itemType: "checkList", ... }
 *
 * const fields2 = getInspectionTypeFields("maintenance");
 * // { checkListField: "maintenance", itemType: "maintenance", ... }
 * ```
 */
export function getInspectionTypeFields(
  projectType: string | null | undefined
): InspectionTypeFields {
  const type = getInspectionType(projectType);
  return INSPECTION_TYPE_FIELDS[type];
}

/**
 * 프로젝트 타입이 유효한지 확인
 */
export function isValidProjectType(projectType: string | null | undefined): boolean {
  if (!projectType) return false;
  const normalized = projectType.trim();
  return (
    // DocxInspectionType (권장)
    normalized === "checklist" ||
    normalized === "maintenance" ||
    // GraphQL enum
    normalized === "PERFORMANCE_CHECK" ||
    normalized === "MAINTENANCE" ||
    // 레거시 한글
    normalized === "성능점검" ||
    normalized === "유지관리"
  );
}
